/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// WALLET ADDRESSES ON FILE AND THEIR SCREENING (v1_825). One reading for the
// decision board, the reconciled case state and the wallet panel.
//
// The rule: an open wallet screening row blocks sign off only when a wallet
// address is on file and has not been screened. The officer may resolve it with
// a reason (case notes and the Audit Record); a resolution covers the addresses
// open when it was given, so a later address opens the row again. With no
// address on file the row stays open, does not block, and reads
// NO_ADDRESS_DETAIL.

export const WALLET_RE = /\b(bc1[a-zA-HJ-NP-Z0-9]{25,90}|[13][a-zA-HJ-NP-Z0-9]{25,34}|0x[a-fA-F0-9]{40}|T[a-zA-HJ-NP-Z0-9]{33})\b/g;
export const NO_ADDRESS_DETAIL = 'No address on file, screen at first transfer';

const norm = (a) => String(a || '').trim().toLowerCase();

// Every address on file for the case: the captured list, any address quoted in
// the report's source of funds or red flags (a follow up answer can land there
// before collected_data), and the Travel Rule wallets. The wallet panel lists
// the same addresses, each with where it came from (walletAddressSources).
// v1_920: a wallet the MLRO withdrew from the case (travelRule.withdrawn, scope
// 'wallet', with a written reason) is no longer on file. It is listed apart,
// with the reason, never silently dropped.
export function walletAddressesOnFile(collectedData, report) {
  const cd = collectedData && typeof collectedData === 'object' ? collectedData : {};
  const r = report && typeof report === 'object' ? report : {};
  const sof = r.sofAssessment || {};
  const text = [sof.fundingRoute, sof.narrative, sof.notes, sof.namedAccount, ...(Array.isArray(r.redFlags) ? r.redFlags.map((f) => (typeof f === 'string' ? f : (f && (f.flag || f.detail)) || '')) : [])].filter(Boolean).join(' ');
  const fromText = text.match(WALLET_RE) || [];
  const fromTr = Array.isArray(r.travelRule && r.travelRule.wallets)
    ? r.travelRule.wallets.map((w) => (typeof w === 'string' ? w : (w && (w.address || w.wallet)) || '')) : [];
  const fromCd = Array.isArray(cd.walletAddresses) ? cd.walletAddresses.map((w) => (typeof w === 'string' ? w : (w && (w.address || w.wallet)) || '')) : [];
  const withdrawn = new Set(withdrawnWallets(r).map((w) => norm(w.address)));
  const seen = new Set(); const out = [];
  for (const a of [...fromCd, ...fromText, ...fromTr].map((x) => String(x || '').trim()).filter(Boolean)) {
    if (seen.has(norm(a)) || withdrawn.has(norm(a))) continue;
    seen.add(norm(a)); out.push(a);
  }
  return out;
}

// v1_920: wallets the MLRO withdrew from the case as a whole (wrong case, not
// the client's), each with its reason, who and when.
export function withdrawnWallets(report) {
  const tr = report && report.travelRule;
  return (tr && Array.isArray(tr.withdrawn) ? tr.withdrawn : []).filter((w) => w && w.scope === 'wallet' && w.address);
}

// v1_920: WHERE EACH ADDRESS ON FILE CAME FROM. The panel headed every address
// "Addresses provided by client", so an address an officer screened, or one a
// Travel Rule action created, read as the client's own (Fig Markets 04/10/26:
// another client's wallet, screened on the wrong case on 09/09, listed as
// provided by the client). Each address now carries every source found:
//   client    collected_data.walletAddresses (the client gave it)
//   document  named in one of the case's own documents (its analysis or assessment)
//   officer   a wallet screening on this case, or a Travel Rule entry an
//             officer action created, with who and when where recorded
//   analysis  quoted only in the report's source of funds or red flags text
// An address with no source found says so; it is never shown as the client's.
export function walletAddressSources({ collectedData, report, walletScreenings, documents } = {}) {
  const cd = collectedData && typeof collectedData === 'object' ? collectedData : {};
  const r = report && typeof report === 'object' ? report : {};
  const ws = Array.isArray(walletScreenings) ? walletScreenings : [];
  const docs = Array.isArray(documents) ? documents : [];
  const fromCd = new Set((Array.isArray(cd.walletAddresses) ? cd.walletAddresses : []).map((w) => norm(typeof w === 'string' ? w : (w && (w.address || w.wallet)))));
  const sof = r.sofAssessment || {};
  const text = [sof.fundingRoute, sof.narrative, sof.notes, sof.namedAccount, ...(Array.isArray(r.redFlags) ? r.redFlags.map((f) => (typeof f === 'string' ? f : (f && (f.flag || f.detail)) || '')) : [])].filter(Boolean).join(' ');
  const inText = new Set((text.match(WALLET_RE) || []).map(norm));
  const trW = Array.isArray(r.travelRule && r.travelRule.wallets) ? r.travelRule.wallets : [];
  const docText = docs.filter((d) => d && !d.is_deleted).map((d) => ({ name: d.file_name || d.document_name || d.doc_type || 'a document', text: norm([typeof d.assessment === 'string' ? d.assessment : JSON.stringify(d.assessment || ''), JSON.stringify(d.analysis || '')].join(' ')) }));
  const day = (t) => { const d = t ? new Date(t) : null; return d && !isNaN(d) ? d.toLocaleDateString('en-GB') : null; };
  const who = (e) => (e ? String(e).split('@')[0] : null);
  return walletAddressesOnFile(cd, r).map((address) => {
    const a = norm(address);
    const sources = [];
    if (fromCd.has(a)) sources.push({ kind: 'client', label: 'Client entered' });
    for (const d of docText) if (d.text.includes(a)) sources.push({ kind: 'document', label: `From document: ${d.name}` });
    for (const s of ws.filter((x) => x && norm(x.address || x.wallet_address) === a)) {
      sources.push({ kind: 'officer', label: `Officer entered (screened${who(s.screened_by) ? ' by ' + who(s.screened_by) : ''}${day(s.created_at) ? ' on ' + day(s.created_at) : ''})` });
    }
    const tw = trW.find((w) => w && norm(w.address) === a);
    if (tw && (tw.addedBy || tw.source === 'screening' || tw.source === 'officer') && !sources.some((x) => x.kind === 'officer')) {
      sources.push({ kind: 'officer', label: `Officer entered (Travel Rule record${who(tw.addedBy) ? ', ' + who(tw.addedBy) : ''}${day(tw.addedAt) ? ', ' + day(tw.addedAt) : ''})` });
    }
    if (!sources.length && inText.has(a)) sources.push({ kind: 'analysis', label: 'Quoted in the analysis only, no client or document source found' });
    if (!sources.length) sources.push({ kind: 'unknown', label: 'Source not recorded, check before relying on it' });
    return { address, sources, clientSourced: sources.some((x) => x.kind === 'client' || x.kind === 'document') };
  });
}

// v1_920: the MLRO recorded the Travel Rule as not applicable (a fiat only
// client), with a reason and the condition that every wallet is verified
// before any first crypto transfer. Wallet steps then do not block sign off;
// the row says so, with the condition.
export function travelRuleNotApplicable(report) {
  const na = report && report.travelRule && report.travelRule.notApplicable;
  return na && typeof na === 'object' && na.reason ? na : null;
}
export const NOT_APPLICABLE_CONDITION = 'Every wallet is verified (screened, and ownership proved) before any first crypto transfer.';

export function isScreened(address, walletScreenings) {
  return (Array.isArray(walletScreenings) ? walletScreenings : []).some((s) => s && norm(s.address || s.wallet_address) === norm(address));
}

// A resolution covers the addresses it names. One recorded before v1_825 names
// none; it was an officer's recorded reason on the same row, so it stands.
export function resolutionCovers(resolution, unscreened) {
  if (!resolution) return false;
  if (!Array.isArray(resolution.addresses)) return true;
  const covered = new Set(resolution.addresses.map(norm));
  return (unscreened || []).every((a) => covered.has(norm(a)));
}

// The wallet screening row, before any officer resolution.
export function walletRow({ collectedData, report, walletScreenings, hasCryptoIndication }) {
  const ws = Array.isArray(walletScreenings) ? walletScreenings : [];
  const onFile = walletAddressesOnFile(collectedData, report);
  const unscreened = onFile.filter((a) => !isScreened(a, ws));
  const base = { key: 'wallets', q: 'Wallet screening (Chainalysis)', onFile, unscreened };
  const na = travelRuleNotApplicable(report);
  if (na) {
    return { ...base, done: true, blocking: false, notApplicable: true,
      detail: `Travel Rule recorded not applicable (fiat only): ${na.reason}. Condition: ${na.condition || NOT_APPLICABLE_CONDITION}${unscreened.length ? ` ${unscreened.length} address${unscreened.length > 1 ? 'es' : ''} on file not yet screened.` : ''}` };
  }
  if (unscreened.length > 0) {
    return { ...base, done: false, blocking: true, resolvable: true,
      detail: `${unscreened.length} address${unscreened.length > 1 ? 'es' : ''} on file not yet screened${ws.length ? `, ${ws.length} screened` : ''}` };
  }
  if (ws.length > 0) return { ...base, done: true, blocking: false, detail: `${ws.length} wallet${ws.length > 1 ? 's' : ''} screened` };
  if (hasCryptoIndication) return { ...base, done: false, blocking: false, detail: NO_ADDRESS_DETAIL };
  return { ...base, done: true, blocking: false, detail: 'No crypto wallets declared' };
}

export const walletBlockerLabel = (n) => `${n} wallet address${n > 1 ? 'es' : ''} on file not yet screened`;
